import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs } from 'antd';
import { PureQueryOptions } from '@apollo/client';

import { IBrowserConversation, IGroupOverview } from 'core/hooks';
import {
  CommunicatorCtx,
  CommunicatorTab,
} from 'core/utils/communicatorDeepLink';

import { CommunicationsPanel } from '../_detailShared/CommunicationsPanel';
import { SlackMessagesPanel } from '../_detailShared/SlackMessagesPanel';
import { TaskBoard } from '../_detailShared/TaskBoard';

import { GroupMailList } from './GroupMailList';
import * as S from './Communicator.styled';

interface IContextTabsProps {
  groupId: string;
  studentId: string;
  ctx: CommunicatorCtx;
  tab: CommunicatorTab;
  conv: string;
  mailThreads: IBrowserConversation[];
  commsLoading: boolean;
  extraRefetchQueries: PureQueryOptions[];
  /** Unfiltered rollup for the TaskBoard create-form group picker. */
  pickerGroups: IGroupOverview[];
  onTab: (tab: CommunicatorTab) => void;
  onOpenMailThread: (thread: IBrowserConversation) => void;
}

/**
 * Main pane of the communicator: Slack | Почта | Задачи for the selected
 * context. Three contexts share the tab bar:
 *  - student        → their conversations (Slack / mail split) + their tasks;
 *  - group channels → the group's channel conversations + group tasks;
 *  - whole group    → the unified Slack feed, the group mail digest, group tasks.
 */
export const ContextTabs = memo(
  ({
    groupId,
    studentId,
    ctx,
    tab,
    conv,
    mailThreads,
    commsLoading,
    extraRefetchQueries,
    pickerGroups,
    onTab,
    onOpenMailThread,
  }: IContextTabsProps) => {
    const { t } = useTranslation();

    const openTasks = useCallback(() => onTab('tasks'), [onTab]);

    if (!groupId) {
      return <S.EmptyState>{t('pages.communicator.selectGroup')}</S.EmptyState>;
    }

    const isChannels = ctx === 'channels';
    // Remount the conversation panels when the context or deep-linked
    // conversation changes — `initialConversationId` is mount-time state.
    const panelKey = `${
      studentId || (isChannels ? 'channels' : 'group')
    }-${conv}`;

    return (
      <Tabs activeKey={tab} onChange={(key) => onTab(key as CommunicatorTab)}>
        <Tabs.TabPane key="slack" tab={t('pages.communicator.tabs.slack')}>
          {studentId ? (
            <CommunicationsPanel
              key={panelKey}
              studentId={studentId}
              kindFilter="slack"
              initialConversationId={conv || undefined}
              extraRefetchQueries={extraRefetchQueries}
              onOpenTasks={openTasks}
            />
          ) : isChannels ? (
            <CommunicationsPanel
              key={panelKey}
              groupId={groupId}
              kindFilter="slack"
              initialConversationId={conv || undefined}
              extraRefetchQueries={extraRefetchQueries}
              onOpenTasks={openTasks}
            />
          ) : (
            <SlackMessagesPanel groupId={groupId} />
          )}
        </Tabs.TabPane>

        {!isChannels && (
          <Tabs.TabPane key="mail" tab={t('pages.communicator.tabs.mail')}>
            {studentId ? (
              <CommunicationsPanel
                key={panelKey}
                studentId={studentId}
                kindFilter="email"
                initialConversationId={conv || undefined}
                extraRefetchQueries={extraRefetchQueries}
                onOpenTasks={openTasks}
              />
            ) : (
              <GroupMailList
                threads={mailThreads}
                loading={commsLoading}
                onOpen={onOpenMailThread}
              />
            )}
          </Tabs.TabPane>
        )}

        <Tabs.TabPane key="tasks" tab={t('pages.communicator.tabs.tasks')}>
          <TaskBoard
            groupId={studentId ? undefined : groupId}
            studentId={studentId || undefined}
            showCreateForm
            groups={pickerGroups}
          />
        </Tabs.TabPane>
      </Tabs>
    );
  },
);

ContextTabs.displayName = 'ContextTabs';
